import { PRODUCTS_PER_PAGE, TYPES_PER_PAGE } from '@/framework/client/variables';
import { Grid } from '@/components/products/grid';
import { useInfiniteHits, useInstantSearchContext } from 'react-instantsearch';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Product } from '@/types';
import type { HomeListContext } from '@/lib/gtm';
import { useRouter } from 'next/router';
import { useTypes } from '@/framework/type';

interface Props {
  className?: string;
  variables: any;
  column?: any;
  gridClassName?: string;
  listContext?: HomeListContext;
  priorityCount?: number;
  /** Render nothing until the first real search response (a server-rendered grid covers it). */
  hideUntilLive?: boolean;
  /** Called before paint once the first real search response is rendered. */
  onLiveReady?: () => void;
}
export default function ProductGridHome({
  className,
  column,
  gridClassName,
  listContext,
  priorityCount,
  hideUntilLive = false,
  onLiveReady,
}: Readonly<Props>) {
  const { isLastPage, showMore, items, results } = useInfiniteHits({
    showPrevious: true,
  });
  const { status } = useInstantSearchContext();
  const [isLoading, setIsLoading] = useState(true);
  const hasLiveResults =
    status === 'idle' &&
    Boolean(results) &&
    !(results as { __isArtificial?: boolean }).__isArtificial;

  useLayoutEffect(() => {
    if (hasLiveResults) onLiveReady?.();
  }, [hasLiveResults, onLiveReady]);
  const router = useRouter() as any;
  const { types: groups }: any = useTypes({
    limit: TYPES_PER_PAGE,
  });

  useEffect(() => {
    setIsLoading(results?.query === '' || status !== 'idle');
  }, [status, results]);

  const currentPageGroupId =
    router?.components?.[router.pathname]?.props?.pageProps?.variables
      ?.group_id || null;

  const categoryName = useMemo(() => {
    return groups?.find((type: any) => +type?.id === +currentPageGroupId)?.name;
  }, [groups, currentPageGroupId]);

  // TODO: review refresh page to remove filter or back to initial page

  if (hideUntilLive && !hasLiveResults) return null;

  return (
    <Grid
      products={items as unknown as Product[] | undefined}
      loadMore={showMore}
      isLoading={isLoading}
      isLoadingMore={isLoading}
      hasMore={!isLastPage}
      error={null}
      limit={PRODUCTS_PER_PAGE}
      className={className}
      gridClassName={gridClassName}
      column={column}
      categoryName={categoryName}
      listContext={listContext}
      priorityCount={priorityCount}
    />
  );
}
